import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import Swal from "sweetalert2";
import DataTableComponent from "../../tools/DataTableComponent";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./Articles.css";

const baseUrl = process.env.REACT_APP_BASE_URL;


const validationSchema = Yup.object({
    code: Yup.string()
        .required("Le code est obligatoire"),

    nameLat: Yup.string()
        .required("La désignation est obligatoire"),

    familyId: Yup.number()
        .moreThan(0, "Famille obligatoire")
        .required(),

    subFamilyId: Yup.number()
        .moreThan(0, "Sous-famille obligatoire")
        .required(),

    brandId: Yup.number()
        .moreThan(0, "Marque obligatoire")
        .required(),

    unitOfMeasurementId: Yup.number()
        .moreThan(0, "Unité obligatoire")
        .required(),

    purchasePrice: Yup.number()
        .min(0, "Prix invalide")
        .required("Prix d'achat obligatoire"),

    salePrice: Yup.number()
        .min(0, "Prix invalide")
        .required("Prix de vente obligatoire"),

    tva: Yup.number()
        .min(0, "TVA invalide")
        .max(100, "TVA invalide")
        .nullable(),

    descriptionLat: Yup.string()
        .nullable()
});


const Articles = () => {
    const [articles, setArticles] = useState([]);
    const [families, setFamilies] = useState([]);
    const [subFamilies, setSubFamilies] = useState([]);
    const [brands, setBrands] = useState([]);
    const [units, setUnits] = useState([]);

    const initialValues = {
        id: 0,
        code: "",
        nameAr: "",
        nameLat: "",
        descriptionAr: "",
        descriptionLat: "",
        familyId: 0,
        familyName: "",
        subFamilyId: 0,
        subFamilyName: "",
        brandId: 0,
        brandName: "",
        unitOfMeasurementId: 0,
        unitOfMeasurementName: "",
        codeBar: "",
        purchasePrice: "",
        salePrice: "",
        tva: "",
        margin: "",
        stockQuantity: "",
        quantityMin: "",
        quantityMax: ""
    };

    const [selectedArticle, setSelectedArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem("token");

    useEffect(() => {
        fetchArticles();
        fetchDependencies();
    }, []);

    const fetchDependencies = async () => {
        try {
            const [familiesRes, subFamiliesRes, brandsRes, unitsRes] = await Promise.all([
                axiosInstance.get(`${baseUrl}/api/Families`),
                axiosInstance.get(`${baseUrl}/api/SubFamilies`),
                axiosInstance.get(`${baseUrl}/api/Brands`),
                axiosInstance.get(`${baseUrl}/api/UnitsOfMeasurement`),
            ]);
            setFamilies(familiesRes.data);
            setSubFamilies(subFamiliesRes.data);
            setBrands(brandsRes.data);
            setUnits(unitsRes.data);
        } catch (err) {
            console.log("Erreur de chargement des dépendances");
        }
    };

    const fetchArticles = () => {
        axiosInstance.get(`${baseUrl}/api/Articles`)
            .then(res => setArticles(res.data))
            .catch(() => console.log("Impossible de charger les articles"))
            .finally(() => setLoading(false));
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values, { setErrors, resetForm }) => {

            const url = values.id
                ? `${baseUrl}/api/Articles/UpdateArticle`
                : `${baseUrl}/api/Articles/CreateArticle`;

            const payload = {
                ...values,
                id: parseInt(values.id, 10),
                familyId: parseInt(values.familyId, 10),
                subFamilyId: parseInt(values.subFamilyId, 10),
                brandId: parseInt(values.brandId, 10),
                unitOfMeasurementId: parseInt(values.unitOfMeasurementId, 10),
                purchasePrice: parseFloat(values.purchasePrice || 0),
                salePrice: parseFloat(values.salePrice || 0),
                tva: values.tva === "" ? null : parseFloat(values.tva),
                margin: values.margin === "" ? null : parseFloat(values.margin),
                stockQuantity: parseInt(values.stockQuantity || 0, 10),
                quantityMin: values.quantityMin === "" ? null : parseInt(values.quantityMin, 10),
                quantityMax: values.quantityMax === "" ? null : parseInt(values.quantityMax, 10),
            };

            try {
                await axiosInstance.post(url, payload);

                window.$("#modalForm").modal("hide");

                Swal.fire({
                    toast: true,
                    position: "top-end",
                    icon: "success",
                    title: "Enregistrement effectué avec succès",
                    showConfirmButton: false,
                    timer: 2000,
                });

                fetchArticles();
                resetForm();
                setSelectedArticle(null);

            } catch (error) {

                // 🔴 erreurs ServiceResponse
                if (error.response?.data?.errors) {
                    setErrors(error.response.data.errors);
                } else {
                    Swal.fire({
                        toast: true,
                        position: "top-end",
                        icon: "error",
                        title: "Opération échouée",
                        showConfirmButton: false,
                        timer: 2000,
                    });
                }
            }
        }
    });
    const handleEdit = (article) => {
        setSelectedArticle(article);
        formik.setValues({
            ...article,
            tva: article.tva ?? "",
        });

        setTimeout(() => {
            window.$("#modalForm").modal("show");
        }, 100);
    };


    const handleDelete = (id) => {
        Swal.fire({
            title: "Êtes-vous sûr ?",
            text: "Cette action est irréversible !",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, supprimer"
        }).then((result) => {
            if (result.isConfirmed) {
                axiosInstance.post(`${baseUrl}/api/Articles/DeleteArticle`, { 'id': id, 'idString': '' })
                    .then(() => fetchArticles());
            }
        });
    };

    const resetForm = () => {
        formik.resetForm();
        setSelectedArticle(null);
    };

    return (
        <div className="container-fluid mt-4">
            <h3 className="text-center my-3">Gestion des Articles</h3>
            <button className="btn btn-primary btn-ajouter mb-3" data-toggle="modal" data-target="#modalForm" onClick={resetForm}>
                <i className="fas fa-plus"></i> Ajouter
            </button>

            {loading ? (
                <p>Chargement...</p>
            ) : (
                <DataTableComponent
                    title="Liste des articles"
                    columns={["code", "nameLat", "stockQuantity", "salePrice", "tva"]}
                    columnsMapping={{
                        nameLat: "Désignation",
                        code: "Code",
                        stockQuantity: "Stock",
                        salePrice: "Prix de vente",
                        tva: "TVA(%)"
                    }}
                    keyField="id"
                    data={articles}
                    handleEdit={handleEdit}
                    handleDelete={(id) => handleDelete(id)}
                />
            )}

            <div className="modal fade" id="modalForm">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5>{selectedArticle ? "Modifier" : "Ajouter"} un article</h5>
                            <button type="button" className="close" data-dismiss="modal">
                                <span>&times;</span>
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="form-group">
                                <label>Code</label>
                                <input
                                    type="text"
                                    name="code"
                                    className={`form-control ${formik.touched.code && formik.errors.code ? "is-invalid" : ""}`}
                                    value={formik.values.code}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                <div className="invalid-feedback">
                                    {formik.errors.code}
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Désignation</label>
                                <input
                                    type="text"
                                    name="nameLat"
                                    className={`form-control ${formik.touched.nameLat && formik.errors.nameLat ? "is-invalid" : ""}`}
                                    value={formik.values.nameLat}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                <div className="invalid-feedback">
                                    {formik.errors.nameLat}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group col-md-6">
                                    <label>Famille</label>
                                    <select
                                        name="familyId"
                                        className={`form-control ${formik.touched.familyId && formik.errors.familyId ? "is-invalid" : ""}`}
                                        value={formik.values.familyId}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    >
                                        <option value={0}>-- Sélectionnez --</option>
                                        {families.map(f => (
                                            <option key={f.id} value={f.id}>{f.nameLat}</option>
                                        ))}
                                    </select>
                                    <div className="invalid-feedback">
                                        {formik.errors.familyId}
                                    </div>

                                </div>
                                <div className="form-group col-md-6">
                                    <label>Sous Famille</label>
                                    <select
                                        name="subFamilyId"
                                        className={`form-control ${formik.touched.subFamilyId && formik.errors.subFamilyId ? "is-invalid" : ""}`}
                                        value={formik.values.subFamilyId}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    >
                                        <option value={0}>-- Sélectionnez --</option>
                                        {subFamilies.map(f => (
                                            <option key={f.id} value={f.id}>{f.nameLat}</option>
                                        ))}
                                    </select>
                                    <div className="invalid-feedback">
                                        {formik.errors.subFamilyId}
                                    </div>

                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group col-md-6">
                                    <label>Marque</label>
                                    <select
                                        name="brandId"
                                        className={`form-control ${formik.touched.brandId && formik.errors.brandId ? "is-invalid" : ""}`}
                                        value={formik.values.brandId}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    >
                                        <option value={0}>-- Sélectionnez --</option>
                                        {brands.map(f => (
                                            <option key={f.id} value={f.id}>{f.nameLat}</option>
                                        ))}
                                    </select>
                                    <div className="invalid-feedback">
                                        {formik.errors.brandId}
                                    </div>
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Unité</label>
                                    <select
                                        name="unitOfMeasurementId"
                                        className={`form-control ${formik.touched.unitOfMeasurementId && formik.errors.unitOfMeasurementId ? "is-invalid" : ""}`}
                                        value={formik.values.unitOfMeasurementId}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    >
                                        <option value={0}>-- Sélectionnez --</option>
                                        {units.map(f => (
                                            <option key={f.id} value={f.id}>{f.nameLat}</option>
                                        ))}
                                    </select>
                                    <div className="invalid-feedback">
                                        {formik.errors.unitOfMeasurementId}
                                    </div>
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group col-md-4">
                                    <label>Prix achat</label>
                                    <input
                                        type="number"
                                        name="purchasePrice"
                                        className={`form-control ${formik.touched.purchasePrice && formik.errors.purchasePrice ? "is-invalid" : ""}`}
                                        value={formik.values.purchasePrice}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="invalid-feedback">
                                        {formik.errors.purchasePrice}
                                    </div>
                                </div>
                                <div className="form-group col-md-4">
                                    <label>Prix vente</label>
                                    <input
                                        type="number"
                                        name="salePrice"
                                        className={`form-control ${formik.touched.salePrice && formik.errors.salePrice ? "is-invalid" : ""}`}
                                        value={formik.values.salePrice}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="invalid-feedback">
                                        {formik.errors.salePrice}
                                    </div>
                                </div>
                                <div className="form-group col-md-4">
                                    <label>TVA (%)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        name="tva"
                                        className={`form-control ${formik.touched.tva && formik.errors.tva ? "is-invalid" : ""}`}
                                        value={formik.values.tva || ""}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder="ex: 20"

                                    />
                                    <div className="invalid-feedback">
                                        {formik.errors.tva}
                                    </div>
                                </div>


                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea
                                            name="descriptionLat"
                                            value={formik.values.descriptionLat}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            rows={6}
                                            placeholder="Saisissez les spécifications techniques du produit..."
                                            className={`form-control font-monospace ${formik.touched.descriptionLat && formik.errors.descriptionLat
                                                ? "is-invalid"
                                                : ""
                                                }`}
                                        />

                                        <div className="invalid-feedback">
                                            {formik.errors.descriptionLat}
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                            <button className="btn btn-enregistrer" onClick={formik.handleSubmit}>Sauvegarder</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Articles;
